import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toastr from 'react-hot-toast';
import {
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import type { KeyObjective } from 'types/entityTypes/KeyObjectives';
import type { MainChallenge } from 'types/entityTypes/MainChallenges';
import type { Country } from 'types/Countries';
import type { BusinessIndustry } from 'types/Industries';
import React from 'react';
import {
  SinglePersonalOverviewResponse,
  PersonalOverviewKeyObjective,
  PersonalOverviewMainChallenge,
} from 'types/PersonalOverview';

export const usePersonalOverviewByID = (
  personalOverviewID: string | undefined
) => {
  const getCompanyOverviewByID = () => {
    const queryArray = [
      generateIncludeQuery([
        'personalOverviewKeyObjectives.keyObjective',
        'personalOverviewMainChallenges.mainChallenge',
        // 'businessIndustry',
      ]),
    ];

    const url = generateURL(
      SPARK_RESOURCE_TYPES.PERSONAL_OVERVIEWS,
      queryArray,
      personalOverviewID
    );
    return fetchWrapper<SinglePersonalOverviewResponse>({
      url,
      method: 'GET',
    });
  };
  const response = useQuery(
    ['personalOverview', personalOverviewID],
    getCompanyOverviewByID,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to fetch company overview: ${error.message}`);
        }
      },
      enabled: Boolean(personalOverviewID),
      ...CACHE_AND_STALE_TIME,
    }
  );
  const included = response.data?.included;

  const keyObjectives = React.useMemo(
    () =>
      filterIncludedItem<KeyObjective>(
        included,
        SPARK_RESOURCE_TYPES.KEY_OBJECTIVES
      ),
    [included]
  );
  const personalOverviewKeyObjectives = React.useMemo(
    () =>
      filterIncludedItem<PersonalOverviewKeyObjective>(
        included,
        SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_KEY_OBJECTIVES
      ),
    [included]
  );

  const personalOverviewMainChallenges = React.useMemo(
    () =>
      filterIncludedItem<PersonalOverviewMainChallenge>(
        included,
        SPARK_RESOURCE_TYPES.COMPANY_OVERVIEW_MAIN_CHALLENGES
      ),
    [included]
  );

  const mainChallenges = React.useMemo(
    () =>
      filterIncludedItem<MainChallenge>(
        included,
        SPARK_RESOURCE_TYPES.MAIN_CHALLENGES
      ),
    [included]
  );

  const businessIndustry = React.useMemo(
    () =>
      filterIncludedItem<BusinessIndustry>(
        included,
        SPARK_RESOURCE_TYPES.BUSINESS_INDUSTRIES
      )?.[0],
    [included]
  );

  const country = React.useMemo(
    () =>
      filterIncludedItem<Country>(
        included,
        SPARK_RESOURCE_TYPES.COUNTRIES
      )?.[0],
    [included]
  );
  const personalOverview = response.data?.data;
  return {
    mainChallenges,
    personalOverviewMainChallenges,
    keyObjectives,
    personalOverviewKeyObjectives,
    businessIndustry,
    country,
    personalOverview,
    isLoading: response.isLoading,
  };
};
